/**
 * Estimativa ANTES de disparar: custo exato, retorno esperado, duracao e o
 * risco para o numero do comercial. Regras puras, sem React.
 *
 * Decisoes nao obvias:
 *  1. CUSTO e conta exata (contatos x tarifa); RETORNO e expectativa. Por
 *     isso o retorno so aparece com historico: soma os envios anteriores da
 *     empresa para os publicos escolhidos (`benchmark`) e aplica a mesma taxa
 *     ao publico de agora. Sem historico, `retorno` e `null` e a tela diz
 *     que a estimativa aparece depois do primeiro envio -- inventar uma taxa
 *     "de mercado" seria o numero mais perigoso da tela.
 *  2. A comissao esperada usa o `feeBps` da empresa sobre o recuperado
 *     esperado. E estimativa de exibicao: a comissao real e do servidor
 *     (ADR-001), creditada ao ultimo contato em ate 7 dias.
 *  3. DURACAO respeita o ritmo, a janela diaria e, na API oficial, o limite
 *     de conversas por dia da Meta. O que limitar mais manda.
 *  4. RISCO e o pior de cinco fatores explicados (ritmo, janela, opt-out
 *     recente, volume para o aquecimento do numero, categoria). Cada fator
 *     diz o que fazer, nao so o nivel.
 */

import { formatCount, formatPercentFromBps } from '../../lib/format';
import { minutosDaJanela } from './formulario';
import type { NumberHealth, Segment, SenderKind, TemplateCategory, WhatsAppTariffs } from './types';

export function tarifaPorMensagemCents(kind: SenderKind, category: TemplateCategory, tariffs: WhatsAppTariffs): number {
  if (kind === 'evolution') {
    return 0;
  }
  return category === 'marketing' ? tariffs.officialMarketingCents : tariffs.officialUtilityCents;
}

export interface RetornoEsperado {
  readonly baseEnvios: number;
  readonly baseMensagens: number;
  /** Parte do publico escolhido sem historico (estimativa parcial). */
  readonly parcial: boolean;
  readonly respostas: number;
  readonly pagamentos: number;
  readonly recuperadoCents: number;
  readonly comissaoCents: number;
}

export interface Duracao {
  readonly minutos: number;
  readonly dias: number;
  readonly limitadoPor: 'ritmo' | 'limite_diario';
}

export interface EstimativaEnvio {
  readonly contatos: number;
  readonly tarifaCents: number;
  readonly custoCents: number;
  readonly retorno: RetornoEsperado | null;
  readonly custoPorRecuperacaoCents: number | null;
  readonly duracao: Duracao | null;
}

export function estimarEnvio(entrada: {
  readonly contatos: number;
  /** Segmentos escolhidos; vazio no CSV. */
  readonly segmentos: readonly Segment[];
  readonly senderKind: SenderKind | null;
  readonly category: TemplateCategory | null;
  readonly tariffs: WhatsAppTariffs;
  readonly feeBps: number;
  readonly porMinuto: number;
  readonly janelaInicio: string;
  readonly janelaFim: string;
  readonly dailyLimit: number | null;
}): EstimativaEnvio {
  const { contatos } = entrada;
  const tarifaCents = entrada.senderKind === null || entrada.category === null ? 0 : tarifaPorMensagemCents(entrada.senderKind, entrada.category, entrada.tariffs);
  const custoCents = contatos * tarifaCents;

  const comHistorico = entrada.segmentos.filter((s) => s.benchmark !== null && s.benchmark.sent > 0);
  let retorno: RetornoEsperado | null = null;
  if (contatos > 0 && comHistorico.length > 0) {
    const soma = comHistorico.reduce(
      (acc, s) => {
        const b = s.benchmark;
        return b === null
          ? acc
          : {
              envios: acc.envios + b.broadcasts,
              enviadas: acc.enviadas + b.sent,
              respostas: acc.respostas + b.replied,
              pagos: acc.pagos + b.paid,
              recuperado: acc.recuperado + b.recoveredAmountCents,
            };
      },
      { envios: 0, enviadas: 0, respostas: 0, pagos: 0, recuperado: 0 },
    );
    const recuperadoCents = Math.round((contatos * soma.recuperado) / soma.enviadas);
    retorno = {
      baseEnvios: soma.envios,
      baseMensagens: soma.enviadas,
      parcial: comHistorico.length < entrada.segmentos.length,
      respostas: Math.round((contatos * soma.respostas) / soma.enviadas),
      pagamentos: Math.round((contatos * soma.pagos) / soma.enviadas),
      recuperadoCents,
      comissaoCents: Math.round((recuperadoCents * entrada.feeBps) / 10_000),
    };
  }

  const janela = minutosDaJanela(entrada.janelaInicio, entrada.janelaFim);
  let duracao: Duracao | null = null;
  if (contatos > 0 && entrada.porMinuto > 0 && janela > 0) {
    const minutos = Math.ceil(contatos / entrada.porMinuto);
    const diasPeloRitmo = Math.ceil(minutos / janela);
    const diasPeloLimite = entrada.dailyLimit === null || entrada.dailyLimit <= 0 ? 1 : Math.ceil(contatos / entrada.dailyLimit);
    duracao = { minutos, dias: Math.max(diasPeloRitmo, diasPeloLimite), limitadoPor: diasPeloLimite > diasPeloRitmo ? 'limite_diario' : 'ritmo' };
  }

  return {
    contatos,
    tarifaCents,
    custoCents,
    retorno,
    custoPorRecuperacaoCents: retorno === null || retorno.pagamentos <= 0 ? null : Math.round(custoCents / retorno.pagamentos),
    duracao,
  };
}

/** "2 h 10 min" / "3 dias úteis de janela": duracao em palavras. */
export function descreverDuracao(d: Duracao, janelaInicio: string, janelaFim: string): string {
  if (d.dias > 1) {
    const motivo = d.limitadoPor === 'limite_diario' ? 'pelo limite diário da Meta' : `na janela das ${janelaInicio} às ${janelaFim}`;
    return `${formatCount(d.dias)} dias ${motivo}`;
  }
  if (d.minutos < 60) {
    return `cerca de ${formatCount(d.minutos)} min`;
  }
  const h = Math.floor(d.minutos / 60);
  const m = d.minutos % 60;
  return m === 0 ? `cerca de ${h} h` : `cerca de ${h} h ${m} min`;
}

// -----------------------------------------------------------------------------
// Risco para o numero
// -----------------------------------------------------------------------------

export type NivelRisco = 'baixo' | 'moderado' | 'alto';

export interface FatorRisco {
  readonly chave: 'ritmo' | 'janela' | 'optout' | 'volume' | 'categoria';
  readonly rotulo: string;
  readonly valor: string;
  readonly nivel: NivelRisco;
  readonly explicacao: string;
}

export interface AvaliacaoRisco {
  readonly nivel: NivelRisco;
  readonly fatores: readonly FatorRisco[];
  /** A primeira coisa a mudar, quando ha. */
  readonly recomendacao: string | null;
}

const PESO: Readonly<Record<NivelRisco, number>> = { baixo: 0, moderado: 1, alto: 2 };

function pior(a: NivelRisco, b: NivelRisco): NivelRisco {
  return PESO[b] > PESO[a] ? b : a;
}

export function avaliarRisco(entrada: {
  readonly senderKind: SenderKind | null;
  readonly health: NumberHealth | null;
  readonly porMinuto: number;
  readonly janelaInicio: string;
  readonly janelaFim: string;
  readonly contatos: number;
  readonly category: TemplateCategory | null;
}): AvaliacaoRisco {
  const oficial = entrada.senderKind === 'oficial';
  const fatores: FatorRisco[] = [];

  const [ritmoBaixo, ritmoAlto] = oficial ? [60, 120] : [20, 40];
  const nivelRitmo: NivelRisco = entrada.porMinuto <= ritmoBaixo ? 'baixo' : entrada.porMinuto <= ritmoAlto ? 'moderado' : 'alto';
  fatores.push({
    chave: 'ritmo',
    rotulo: 'Ritmo',
    valor: `${formatCount(entrada.porMinuto)} por minuto`,
    nivel: nivelRitmo,
    explicacao:
      nivelRitmo === 'baixo'
        ? 'Ritmo de conversa: o WhatsApp não enxerga rajada.'
        : nivelRitmo === 'moderado'
          ? `Acima de ${ritmoBaixo}/min ${oficial ? 'a Meta' : 'o WhatsApp'} começa a tratar como disparo automático.`
          : `Rajada é o principal motivo de bloqueio. Baixe para ${ritmoBaixo}/min.`,
  });

  const cedo = entrada.janelaInicio < '08:00';
  const tarde = entrada.janelaFim > '20:30';
  const nivelJanela: NivelRisco = entrada.janelaInicio < '07:00' || entrada.janelaFim > '22:00' ? 'alto' : cedo || tarde ? 'moderado' : 'baixo';
  fatores.push({
    chave: 'janela',
    rotulo: 'Janela',
    valor: `${entrada.janelaInicio} às ${entrada.janelaFim}`,
    nivel: nivelJanela,
    explicacao:
      nivelJanela === 'baixo'
        ? 'Horário comercial: a mensagem chega quando a pessoa pode responder.'
        : 'Mensagem cedo demais ou tarde demais vira denúncia de spam. Prefira das 09:00 às 20:00.',
  });

  const h = entrada.health;
  const nivelOptOut: NivelRisco = h === null ? 'moderado' : h.optOutRate7dBps < 150 ? 'baixo' : h.optOutRate7dBps < 300 ? 'moderado' : 'alto';
  fatores.push({
    chave: 'optout',
    rotulo: 'Opt-out dos últimos 7 dias',
    valor: h === null ? 'sem histórico' : formatPercentFromBps(h.optOutRate7dBps),
    nivel: nivelOptOut,
    explicacao:
      h === null
        ? 'Número sem histórico recente: comece com um público pequeno para medir.'
        : nivelOptOut === 'baixo'
          ? 'Poucos pedidos de SAIR: o número está com boa reputação.'
          : 'Cada SAIR e cada bloqueio derrubam a reputação. Prefira utility e o público mais quente.',
  });

  const janelaMin = minutosDaJanela(entrada.janelaInicio, entrada.janelaFim);
  const porDia = Math.min(entrada.contatos, entrada.porMinuto * janelaMin);
  let nivelVolume: NivelRisco = 'baixo';
  let explicacaoVolume = 'Volume diário dentro do que o número aguenta.';
  let valorVolume = `${formatCount(porDia)} por dia`;
  if (oficial && h !== null && h.dailyLimit !== null) {
    const restante = Math.max(0, h.dailyLimit - h.sentToday);
    valorVolume = `${formatCount(porDia)} de ${formatCount(restante)} livres hoje`;
    if (porDia > restante) {
      nivelVolume = 'moderado';
      explicacaoVolume = 'Passa do limite diário da Meta: o envio continua amanhã sozinho, sem risco de bloqueio.';
    }
  } else if (!oficial) {
    const aquecido = h !== null && h.connectedDays >= 14;
    const recomendado = aquecido ? 1_000 : 250;
    valorVolume = `${formatCount(porDia)} por dia (recomendado até ${formatCount(recomendado)})`;
    if (porDia > recomendado * 2) {
      nivelVolume = 'alto';
      explicacaoVolume = aquecido ? 'Volume muito acima do normal do número. Divida em dias.' : 'Número novo, ainda aquecendo: comece com até 250 por dia.';
    } else if (porDia > recomendado) {
      nivelVolume = 'moderado';
      explicacaoVolume = aquecido ? 'Acima do volume habitual: ritmo baixo compensa.' : 'Número conectado há menos de 14 dias: volume alto pesa mais.';
    }
  }
  fatores.push({ chave: 'volume', rotulo: 'Volume', valor: valorVolume, nivel: nivelVolume, explicacao: explicacaoVolume });

  const nivelCategoria: NivelRisco = entrada.category === 'marketing' && !oficial ? 'moderado' : 'baixo';
  fatores.push({
    chave: 'categoria',
    rotulo: 'Categoria do texto',
    valor: entrada.category === null ? 'sem modelo' : entrada.category === 'marketing' ? 'Marketing' : 'Utility',
    nivel: nivelCategoria,
    explicacao:
      nivelCategoria === 'moderado'
        ? 'Texto promocional num número comum é o que mais gera denúncia. Prefira utility ou a API oficial.'
        : 'Texto de serviço (utility) sobre algo que a pessoa começou: baixo atrito.',
  });

  const nivel = fatores.reduce<NivelRisco>((acc, f) => pior(acc, f.nivel), 'baixo');
  const primeiro = fatores.find((f) => f.nivel === 'alto') ?? fatores.find((f) => f.nivel === 'moderado') ?? null;
  return { nivel, fatores, recomendacao: primeiro === null ? null : primeiro.explicacao };
}
